import { useCallback, useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { usePackingContext } from './usePackingContext';
import { useToast } from '@shared/hooks/use-toast';
import { usePrintingMethod } from '@modules/configuration/Printers/hooks/usePrintingMethod';

export interface SelectedProductInfo {
  id: string;
  code: string;
  name: string;
}

export interface PackingFlowState {
  isPackingMode: boolean;
  selectedProduct: SelectedProductInfo | null;
  selectedOrder: any | null;
  // Cantidades asignadas por orden y producto: key `${orderId}:${productCode}`
  assignedQuantities: Record<string, number>;
  completedOrder: any | null;
  isSendingOrder: boolean;
}

export interface PackingFlowApi extends PackingFlowState {
  selectProduct: (productCode: string) => void;
  addToPacking: (
    productCode: string,
    quantity: number,
    orderId?: number
  ) => void;
  getMissingQtyFor: (productCode: string) => number;
  exitPackingMode: () => void;
  sendOrderPackingData: (orderId: number) => Promise<void>;
  sendPackingData: () => Promise<void>;
  isSendingOrder: boolean;
}

// Hook que concentra la lógica de selección de producto, orden objetivo y cantidades asignadas
export const usePackingFlow = (): PackingFlowApi => {
  const { products, orders, adapter, refetchData } = usePackingContext() as any;
  const { id: preparationId } = useParams<{ id: string }>();
  const { toast } = useToast();
  const { isPrintNode } = usePrintingMethod();

  const [isPackingMode, setIsPackingMode] = useState<boolean>(false);

  // Logs eliminados
  useEffect(() => {
    // noop
  }, [isPackingMode]);
  const [selectedProduct, setSelectedProduct] =
    useState<SelectedProductInfo | null>(null);
  const [selectedOrder, setSelectedOrder] = useState<any | null>(null);
  const [assignedQuantities, setAssignedQuantities] = useState<
    Record<string, number>
  >({});
  const [completedOrder, setCompletedOrder] = useState<any | null>(null);
  const [isSendingOrder, setIsSendingOrder] = useState<boolean>(false);

  // Busca el primer pedido que requiere el producto (qty > qty_done)
  const findFirstOrderNeedingProduct = useCallback(
    (productCode: string) => {
      return (orders || []).find((order: any) => {
        // Excluir órdenes completadas (status_id = 6)
        if (order.status_id === 6) return false;

        // Buscar en líneas de productos individuales
        let line = (order.lines || []).find(
          (l: any) => l.product?.code === productCode
        );

        if (line) {
          const key = `${order.id}:${productCode}`;
          const alreadyAssigned = assignedQuantities[key] || 0;
          const remaining =
            (line.qty || 0) - (line.qty_done || 0) - alreadyAssigned;
          return remaining > 0;
        }

        // Buscar en packs
        line = (order.lines || []).find(
          (l: any) =>
            l.pack &&
            l.pack.lines &&
            l.pack.lines.some(
              (packLine: any) => packLine.product?.code === productCode
            )
        );

        if (line && line.pack) {
          const packLine = line.pack.lines.find(
            (pl: any) => pl.product?.code === productCode
          );
          if (packLine) {
            const key = `${order.id}:${productCode}`;
            const alreadyAssigned = assignedQuantities[key] || 0;
            const remaining =
              (line.qty || 0) - (line.qty_done || 0) - alreadyAssigned;
            return remaining > 0;
          }
        }

        return false;
      });
    },
    [orders, assignedQuantities]
  );

  // Selecciona producto y orden objetivo; habilita modo packing
  const selectProduct = useCallback(
    (productCode: string) => {
      const found = (products || []).find((p: any) => p.code === productCode);

      const selected: SelectedProductInfo = {
        id: (found?.id || productCode).toString(),
        code: productCode,
        name: found?.name || 'Producto',
      };

      setSelectedProduct(selected);
      setIsPackingMode(true);

      // Si ya estamos en modo packing con una orden seleccionada, mantener esa orden
      // Solo buscar una nueva orden si no hay una seleccionada
      if (selectedOrder) {
        // Verificar si la orden actual está completada
        if (selectedOrder.status_id === 6) {
          // La orden está completada, buscar una nueva orden para este producto
          const targetOrder = findFirstOrderNeedingProduct(productCode) || null;
          setSelectedOrder(targetOrder);
          return;
        }

        // Verificar si el producto seleccionado está en la orden actual
        const isProductInCurrentOrder = (selectedOrder.lines || []).some(
          (line: any) => {
            if (line.pack && line.pack.lines) {
              // Es un pack - verificar cada producto del pack
              return line.pack.lines.some(
                (packLine: any) => packLine.product?.code === productCode
              );
            } else if (line.product) {
              // Es un producto individual
              return line.product?.code === productCode;
            }
            return false;
          }
        );

        if (isProductInCurrentOrder) {
          // Mantener la orden actual si el producto está en ella
          return;
        }
      }

      // Solo buscar una nueva orden si no hay una seleccionada o si el producto no está en la orden actual
      const targetOrder = findFirstOrderNeedingProduct(productCode) || null;
      setSelectedOrder(targetOrder);
    },
    [products, findFirstOrderNeedingProduct, selectedOrder]
  );

  // Agrega una cantidad al packing
  const addToPacking = useCallback(
    (productCode: string, quantity: number, orderId?: number) => {
      const targetOrder = orderId
        ? orders?.find((o: any) => o.id === orderId)
        : findFirstOrderNeedingProduct(productCode);

      if (!targetOrder) {
        toast({
          title: 'Error',
          description: 'No se encontró una orden válida para este producto',
          variant: 'destructive',
        });
        return;
      }

      const key = `${targetOrder.id}:${productCode}`;
      const currentQuantity = assignedQuantities[key] || 0;
      const newQuantity = currentQuantity + quantity;

      setAssignedQuantities((prev) => ({
        ...prev,
        [key]: newQuantity,
      }));
    },
    [orders, findFirstOrderNeedingProduct, assignedQuantities, toast]
  );

  const exitPackingMode = useCallback(() => {
    // Logs eliminados
    setIsPackingMode(false);
    setSelectedOrder(null);
    setSelectedProduct(null);
    setCompletedOrder(null);
    setIsSendingOrder(false);

    // Logs eliminados
  }, []);

  // Envía los datos del packing por pedido individual
  const sendOrderPackingData = useCallback(
    async (orderId: number) => {
      if (!preparationId || !adapter) return;

      // Logs eliminados

      // Procesar datos ANTES de activar el loading
      const orderProducts = Object.entries(assignedQuantities)
        .filter(([key]) => key.startsWith(`${orderId}:`))
        .map(([key, quantity]) => {
          const productCode = key.split(':')[1];
          const order = orders?.find((o: any) => o.id === orderId);
          if (!order) return null;

          let productId = null;
          const line = (order.lines || []).find(
            (l: any) => l.product?.code === productCode
          );
          if (line) {
            productId = line.product?.id;
          } else {
            const packLine = (order.lines || []).find((l: any) => {
              if (l.pack && l.pack.lines) {
                return l.pack.lines.some(
                  (packLine: any) => packLine.product?.code === productCode
                );
              }
              return false;
            });
            if (packLine) {
              const packProduct = packLine.pack.lines.find(
                (packLine: any) => packLine.product?.code === productCode
              );
              productId = packProduct?.product?.id;
            }
          }

          return productId
            ? { product_id: productId, quantity: quantity }
            : null;
        })
        .filter(
          (product): product is { product_id: number; quantity: number } =>
            product !== null
        );

      if (orderProducts.length === 0) return;

      // Logs eliminados

      // Activar loading ANTES del PUT
      setIsSendingOrder(true);

      try {
        // Logs eliminados

        // Determinar el código de impresora según el método de impresión
        let printerCode: string;

        if (isPrintNode) {
          // Si es PrintNode, obtener el código de la impresora seleccionada
          const selectedPrinterId = localStorage.getItem('selectedPrinterId');
          if (!selectedPrinterId) {
            throw new Error('No se ha seleccionado una impresora');
          }
          printerCode = selectedPrinterId;
        } else {
          // Si es PDF, usar "pdf" como código
          printerCode = 'pdf';
        }

        // PUT con loading activo
        await adapter.sendOrderPackingData(
          parseInt(preparationId),
          orderId,
          orderProducts,
          printerCode
        );

        // Logs eliminados

        // Limpiar cantidades
        setAssignedQuantities((prev) => {
          const newQuantities = { ...prev };
          Object.keys(newQuantities).forEach((key) => {
            if (key.startsWith(`${orderId}:`)) {
              delete newQuantities[key];
            }
          });
          return newQuantities;
        });

        // Recargar datos
        await refetchData();

        // Logs eliminados

        // Salir del modo packing SOLO cuando el PUT termine exitosamente
        setIsPackingMode(false);
        setSelectedOrder(null);
        setSelectedProduct(null);
        setCompletedOrder(null);
        setIsSendingOrder(false);

        toast({
          title: 'Pedido completado',
          description: `Pedido #${orderId} enviado correctamente`,
        });
      } catch (error: any) {
        console.error('Error sending order packing data:', error);

        // En caso de error, desactivar loading pero mantener en modo packing
        setIsSendingOrder(false);

        // REVERTIR: Limpiar las cantidades asignadas para este pedido (revertir agregado)
        setAssignedQuantities((prev) => {
          const newQuantities = { ...prev };
          Object.keys(newQuantities).forEach((key) => {
            if (key.startsWith(`${orderId}:`)) {
              delete newQuantities[key];
            }
          });
          return newQuantities;
        });

        // Logs eliminados
        setIsPackingMode(false);
        setSelectedOrder(null);
        setSelectedProduct(null);
        setCompletedOrder(null);

        toast({
          title: 'Error',
          description:
            error.message ||
            error.response?.data?.message ||
            'Error al enviar los datos del pedido',
          variant: 'destructive',
        });
      }
    },
    [
      preparationId,
      adapter,
      assignedQuantities,
      orders,
      toast,
      refetchData,
      isPrintNode,
    ]
  );

  // Función para enviar datos de packing (DEPRECATED - mantener por compatibilidad)
  const sendPackingData = useCallback(async () => {
    if (!preparationId || !adapter) return;

    try {
      // Preparar datos para enviar
      const packingData = Object.entries(assignedQuantities).map(
        ([key, quantity]) => {
          const [orderId, productCode] = key.split(':');
          return {
            order_id: parseInt(orderId),
            product_code: productCode,
            quantity: quantity,
          };
        }
      );

      await adapter.sendPackingData(parseInt(preparationId), packingData);
      // setPackingSent(true); // This line is removed

      toast({
        title: 'Packing enviado',
        description: 'Los datos del packing han sido enviados correctamente',
      });

      // Recargar datos después del envío
      await refetchData();
    } catch (error: any) {
      console.error('Error sending packing data:', error);
      toast({
        title: 'Error',
        description: error.message || 'Error al enviar los datos del packing',
        variant: 'destructive',
      });
    }
  }, [preparationId, adapter, assignedQuantities, refetchData, toast]);

  // Detectar cuando la orden actual se completa (todas las líneas sin faltante)
  useEffect(() => {
    if (!selectedOrder) return;
    const lines: any[] = selectedOrder.lines || [];
    const hasRemaining = lines.some((line) => {
      if (line.pack && line.pack.lines) {
        // Para packs, verificar que todos los productos del pack estén completos
        return line.pack.lines.some((packLine: any) => {
          const packProductCode = packLine.product?.code || '';
          const packKey = `${selectedOrder.id}:${packProductCode}`;
          const assigned = assignedQuantities[packKey] || 0;
          const remaining = (line.qty || 0) - assigned;
          return remaining > 0;
        });
      } else {
        // Para productos individuales
        const code = line.product?.code || '';
        const key = `${selectedOrder.id}:${code}`;
        const assigned = assignedQuantities[key] || 0;
        // Usar SOLO memoria para determinar faltante
        const remaining = (line.qty || 0) - assigned;
        return remaining > 0;
      }
    });
    if (!hasRemaining) {
      setCompletedOrder(selectedOrder);
      // No salir automáticamente del modo packing - mantener la pantalla

      // Enviar datos del pedido completado
      sendOrderPackingData(selectedOrder.id);
    }
  }, [assignedQuantities, selectedOrder, preparationId, sendOrderPackingData]);

  // Efecto para limpiar estados cuando se sale del modo packing
  useEffect(() => {
    if (!isPackingMode) {
      // Limpiar estados adicionales cuando se sale del modo packing
      setIsSendingOrder(false);
      setSelectedOrder(null);
      setSelectedProduct(null);
    }
  }, [isPackingMode]);

  // Obtiene el faltante para un producto considerando qty_done y lo ya asignado
  const getMissingQtyFor = useCallback(
    (productCode: string): number => {
      const order = selectedOrder || findFirstOrderNeedingProduct(productCode);
      if (!order) return 0;

      // Buscar en líneas de productos individuales
      let line = (order.lines || []).find(
        (l: any) => l.product?.code === productCode
      );

      if (line) {
        // Producto individual
        const key = `${order.id}:${productCode}`;
        const alreadyAssigned = assignedQuantities[key] || 0;
        const remaining =
          (line.qty || 0) - (line.qty_done || 0) - alreadyAssigned;
        return Math.max(0, remaining);
      }

      // Buscar en packs
      line = (order.lines || []).find(
        (l: any) =>
          l.pack &&
          l.pack.lines &&
          l.pack.lines.some(
            (packLine: any) => packLine.product?.code === productCode
          )
      );

      if (line && line.pack) {
        // Producto dentro de un pack
        const packLine = line.pack.lines.find(
          (pl: any) => pl.product?.code === productCode
        );
        if (packLine) {
          const key = `${order.id}:${productCode}`;
          const alreadyAssigned = assignedQuantities[key] || 0;
          const remaining =
            (line.qty || 0) - (line.qty_done || 0) - alreadyAssigned;
          return Math.max(0, remaining);
        }
      }

      return 0;
    },
    [assignedQuantities, findFirstOrderNeedingProduct, selectedOrder]
  );

  return {
    isPackingMode,
    selectedProduct,
    selectedOrder,
    assignedQuantities,
    completedOrder,
    isSendingOrder,
    selectProduct,
    addToPacking,
    getMissingQtyFor,
    exitPackingMode,
    sendOrderPackingData,
    sendPackingData,
  };
};
